import parallaxVideoDefinitions from './parallaxVideos.js';
import { setParallaxTranslation } from './parallaxMotion.js';
import audioPlayer from './audioPlayer.js';

let activeClickPlayback = null;

function pauseScoreAudio() {
  const index = audioPlayer.currentIndex;
  const track = index < 0 ? null : audioPlayer.tracks[index];
  const wasPlaying = Boolean(track && !track.audio.paused && !track.audio.ended);
  if (wasPlaying) {
    track.audio.pause();
    track.li?.classList.remove('playing');
    audioPlayer.updateButtons();
    audioPlayer.updateWaveformState();
  }
  return { index, wasPlaying };
}

function resumeScoreAudio(state) {
  if (!state?.wasPlaying || audioPlayer.currentIndex !== state.index) return;
  const track = audioPlayer.tracks[state.index];
  if (!track || !track.audio.paused || track.audio.ended) return;
  track.audio.play().catch(() => {});
  track.li?.classList.add('playing');
  audioPlayer.updateButtons();
  audioPlayer.updateWaveformState();
}

function stopClickPlayback(resumeAudio = true) {
  if (!activeClickPlayback) return null;
  const playback = activeClickPlayback;
  activeClickPlayback = null;
  playback.video.pause();
  playback.video.muted = true;
  playback.video.defaultMuted = true;
  playback.button.hidden = false;
  playback.button.setAttribute('aria-label', 'Play video with sound');
  if (resumeAudio) resumeScoreAudio(playback.scoreState);
  return playback.scoreState;
}

const parallaxVideoModule = {
  update: null,
  init(definitions = parallaxVideoDefinitions) {
    const container = document.querySelector('.pages-container');
    const strip = container?.querySelector('.pages-inner');
    if (!container || !strip || !definitions.length) return;

    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const entries = [];

    const pages = Array.from(strip.querySelectorAll('.page'));
    for (const definition of definitions) {
      const page = pages.find(element => element.dataset.id === definition.page);
      if (!page || !definition.src) continue;

      const video = document.createElement('video');
      video.className = 'parallax-video-module';
      video.classList.toggle('parallax-video-module--click-to-play', definition.clickToPlay === true);
      video.src = definition.src;
      if (definition.poster) video.poster = definition.poster;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.autoplay = false;
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      let target = video;
      if (definition.clickToPlay === true) {
        const wrapper = document.createElement('div');
        wrapper.className = 'parallax-video-module__wrapper';
        const button = document.createElement('button');
        button.className = 'parallax-video-module__play';
        button.type = 'button';
        button.setAttribute('aria-label', 'Play video with sound');
        const icon = document.createElement('img');
        icon.src = './img/play-circle.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        button.append(icon);
        wrapper.append(video, button);
        strip.append(wrapper);
        target = wrapper;

        const togglePlayback = async () => {
          if (activeClickPlayback?.video === video) {
            stopClickPlayback();
            return;
          }
          const previousScoreState = stopClickPlayback(false);
          const scoreState = previousScoreState || pauseScoreAudio();
          video.muted = false;
          video.defaultMuted = false;
          try {
            await video.play();
            button.hidden = true;
            button.setAttribute('aria-label', 'Pause video');
            activeClickPlayback = { video, button, scoreState };
          } catch {
            video.muted = true;
            video.defaultMuted = true;
            button.hidden = false;
            resumeScoreAudio(scoreState);
          }
        };
        wrapper.addEventListener('click', togglePlayback);
        video.addEventListener('play', () => {
          button.hidden = true;
          button.setAttribute('aria-label', 'Pause video');
        });
        video.addEventListener('pause', () => {
          button.hidden = false;
          button.setAttribute('aria-label', 'Play video with sound');
          if (activeClickPlayback?.video === video) stopClickPlayback();
        });
      } else {
        strip.append(video);
      }
      entries.push({ definition, page, video, target });

    }

    const update = () => {
      const stripBounds = strip.getBoundingClientRect();
      const viewport = container.getBoundingClientRect();
      const strength = reducedMotion.matches ? 0 :
        Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--image-parallax-amount')) || 0;

      for (const { definition, page, video, target } of entries) {
        const canvas = page.classList.contains('mobile-page-pan--enabled')
          ? page.querySelector('.mobile-page-pan-canvas') : null;
        const desktopLayout = desktop.matches || Boolean(canvas);
        if (target.parentElement !== (canvas || strip)) (canvas || strip).append(target);
        const settings = !desktopLayout ? { ...definition, ...definition.mobile } : definition;
        const bounds = (canvas || page.querySelector('.filmstrip-stage') || page).getBoundingClientRect();
        const percentage = (value, fallback) => Number.parseFloat(value ?? fallback) / 100;
        const videoWidth = bounds.width * percentage(settings.width, 40);
        const mobileLeft = !desktopLayout && definition.mobile?.left != null;
        const rightAligned = settings.right != null && !mobileLeft;
        const horizontalPosition = rightAligned
          ? bounds.width * (1 - percentage(settings.right, 0)) - videoWidth
          : bounds.width * percentage(settings.left, 0);
        target.style.left = `${(canvas ? 0 : bounds.left - stripBounds.left) + horizontalPosition}px`;

        const mobileTop = !desktopLayout && definition.mobile?.top != null;
        const bottomAligned = settings.bottom != null && !mobileTop;
        const verticalPosition = bottomAligned ? 1 - percentage(settings.bottom, 0) : percentage(settings.top, 0);
        target.style.top = `${(canvas ? 0 : bounds.top - stripBounds.top) + bounds.height * verticalPosition}px`;
        target.style.transform = bottomAligned ? 'translateY(-100%)' : 'none';
        target.style.width = `${videoWidth}px`;

        const distance = desktopLayout ? viewport.left - bounds.left : viewport.top - bounds.top;
        const override = settings.parallax == null ? NaN : Number(settings.parallax);
        const videoStrength = reducedMotion.matches ? 0 : Number.isFinite(override) ? override : strength;
        const displacement = distance * videoStrength;
        const offset = Number.isFinite(override) ? displacement : Math.max(-80, Math.min(80, displacement));
        setParallaxTranslation(target, offset, desktopLayout, reducedMotion.matches || Boolean(canvas));

        const videoBounds = target.getBoundingClientRect();
        const viewportBounds = container.getBoundingClientRect();
        const visible = videoBounds.right > viewportBounds.left &&
          videoBounds.left < viewportBounds.right &&
          videoBounds.bottom > viewportBounds.top &&
          videoBounds.top < viewportBounds.bottom;
        if (definition.clickToPlay === true) {
          if (visible) {
            video.preload = 'auto';
          } else if (activeClickPlayback?.video === video) {
            stopClickPlayback();
          } else {
            video.pause();
            video.preload = 'none';
          }
        } else {
          video.autoplay = visible;
          if (visible) {
            video.preload = 'auto';
            if (video.paused) video.play().catch(() => {});
          } else {
            video.pause();
            video.preload = 'none';
          }
        }
      }
    };

    this.update = update;
    this.handleScorePlayback = () => stopClickPlayback(false);
    document.addEventListener('score-audio-play', this.handleScorePlayback);
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    reducedMotion.addEventListener('change', update);
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container);
    for (const { page } of entries) resizeObserver.observe(page);
    update();
  },
};

export default parallaxVideoModule;